import React from 'react';
import { Icon } from './components.jsx';
import { fetchEligibleUsers, fmtWhen, upnOf } from './tap.jsx';
// Duo Passes - create a one-time Cisco Duo MFA bypass code for a user who can't
// complete a Duo push (lost/replaced laptop or phone, no signal, travelling).
// Native screen, same shape as Access Passes (TapView): Triage is the front-end
// + audit, and POST /api/duo-bypass makes the signed Duo Admin API call. IT-only
// AND limited to the TAP_ISSUERS allowlist server-side. The code is the fixed
// 000000000, valid 1 hour, single use.
//
// (Replaced the earlier Power-App-in-an-iframe version with a native flow so it
// matches Access Passes and is audited in Triage.)

const { useState, useEffect, useCallback, useRef, useMemo } = React;

const duoApi = (method, path, body) => window.IT_AUTH.api(method, path, body, 'tap');

const BYPASS_CODE = '000000000';

const STATUS_STYLE = {
  created: { color: '#065F46', bg: '#ECFDF5', label: 'Created' },
  refused: { color: '#92400E', bg: '#FFFBEB', label: 'Refused' },
  error:   { color: '#991B1B', bg: '#FEF2F2', label: 'Error' },
};

// Cisco Duo mark - the green shield-check, inlined as SVG (the app CSP blocks
// external images), paired with a "Cisco Duo" wordmark.
function DuoLogo({ height = 24 }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
      <svg viewBox="0 0 24 24" height={height} width={height} role="img" aria-label="Cisco Duo"
        style={{ display: 'block', flex: '0 0 auto' }}>
        <path fill="#6BBE4A" d="M12 1.5 3 5v6.2c0 5.1 3.5 9.9 9 11.3 5.5-1.4 9-6.2 9-11.3V5l-9-3.5z" />
        <path fill="#fff" d="m10.6 15.1-3-3 1.4-1.4 1.6 1.6 4.3-4.3 1.4 1.4-5.7 5.7z" />
      </svg>
      <span style={{ fontSize: height * 0.8, fontWeight: 700, color: 'var(--text)', letterSpacing: 0.2 }}>
        Cisco Duo
      </span>
    </div>
  );
}

export function DuoPassesView({ mobile = false } = {}) {
  const [members, setMembers] = useState(null); // null = loading
  const [membersErr, setMembersErr] = useState(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [issuing, setIssuing] = useState(false);
  const [result, setResult] = useState(null); // { status, targetUpn, detail }
  const [copied, setCopied] = useState(false);
  const [history, setHistory] = useState(null);
  const pickerRef = useRef(null);

  const loadHistory = useCallback(async () => {
    try { setHistory(await duoApi('GET', '/duo-bypass') || []); }
    catch (_) { setHistory([]); }
  }, []);

  useEffect(() => {
    fetchEligibleUsers().then(setMembers).catch((e) => { setMembersErr(e.message); setMembers([]); });
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    const h = (e) => { if (pickerRef.current && !pickerRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const matches = useMemo(() => {
    if (!members) return [];
    const q = query.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) =>
      (m.displayName || '').toLowerCase().includes(q) || upnOf(m).includes(q));
  }, [members, query]);

  const issue = async () => {
    if (!selected || issuing) return;
    setIssuing(true); setResult(null); setCopied(false);
    try {
      const r = await duoApi('POST', '/duo-bypass', { targetUpn: upnOf(selected) });
      setResult(r);
    } catch (e) {
      setResult({ status: 'error', detail: e.message || 'Request failed' });
    } finally {
      setIssuing(false);
      loadHistory();
    }
  };

  const copyCode = () => {
    navigator.clipboard?.writeText(BYPASS_CODE).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const reset = () => { setSelected(null); setQuery(''); setResult(null); setCopied(false); };

  return (
    <div style={{ height: '100%', overflowY: 'auto', background: 'var(--bg)' }}>
      <div style={{ maxWidth: mobile ? '100%' : 760, margin: '0 auto', padding: mobile ? '16px 14px 32px' : '24px 28px', boxSizing: 'border-box' }}>
        <div style={{ marginBottom: 12, display: 'flex', justifyContent: mobile ? 'center' : 'flex-start' }}><DuoLogo height={24}/></div>
        <h1 style={{ margin: 0, fontSize: mobile ? 17 : 20, fontWeight: 700, color: 'var(--text)', textAlign: mobile ? 'center' : 'left' }}>Duo Bypass Code</h1>
        <p style={{ margin: '4px 0 20px', fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5, textAlign: mobile ? 'center' : 'left' }}>
          Create a one-time Duo MFA bypass code for a user who can't complete a Duo push - lost or replaced laptop or phone, no signal, travelling. The code is <code style={{ fontFamily: 'var(--mono)' }}>{BYPASS_CODE}</code>, single use, expires in 1 hour.
        </p>

        {/* Issue card */}
        <div style={{ border: '1px solid var(--line)', borderRadius: 8, background: 'var(--bg-elev)', padding: '16px 18px' }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>User to create a bypass code for</label>
          <div ref={pickerRef} style={{ position: 'relative', marginTop: 6 }}>
            {selected ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '9px 12px', border: '1px solid var(--line)', borderRadius: 6, background: 'var(--bg)' }}>
                <span style={{ fontSize: 14, color: 'var(--text)' }}>
                  {selected.displayName} <span style={{ color: 'var(--text-subtle)', fontFamily: 'var(--mono)', fontSize: 12 }}>{upnOf(selected)}</span>
                </span>
                <button className="btn" onClick={reset} title="Clear"><Icon name="close" size={12}/></button>
              </div>
            ) : (
              <input
                type="text" value={query}
                onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                onFocus={() => setOpen(true)}
                placeholder={members === null ? 'Loading users…' : 'Search by name or email'}
                disabled={members === null && !membersErr}
                style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', border: '1px solid var(--line)', borderRadius: 6, fontSize: mobile ? 16 : 14, background: 'var(--bg-elev)', color: 'var(--text)', outline: 'none' }}
              />
            )}
            {open && !selected && matches.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 6, boxShadow: 'var(--shadow-pop)', maxHeight: 280, overflowY: 'auto', zIndex: 20 }}>
                {matches.map((m) => (
                  <div key={m.id} onClick={() => { setSelected(m); setOpen(false); }}
                    style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--line)' }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{m.displayName}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-subtle)' }}>{upnOf(m)}{m.jobTitle ? ` · ${m.jobTitle}` : ''}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {membersErr && <div style={{ fontSize: 12, color: 'var(--pri-high)', marginTop: 6 }}>Couldn’t load users: {membersErr}</div>}

          <div style={{ marginTop: 14 }}>
            <button className="btn primary" onClick={issue} disabled={!selected || issuing}>
              {issuing ? 'Creating…' : 'Create bypass code'}
            </button>
          </div>
        </div>

        {/* Result */}
        {result && result.status === 'created' && (
          <div style={{ marginTop: 16, border: '1px solid #A7F3D0', background: '#F0FDF4', borderRadius: 8, padding: '16px 18px' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#166534', marginBottom: 10 }}>✓ Bypass code created for {result.targetUpn}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <code style={{ fontSize: 20, fontWeight: 700, letterSpacing: 2, fontFamily: 'var(--mono)', color: '#166534', background: '#fff', border: '1px solid #A7F3D0', borderRadius: 6, padding: '8px 14px' }}>
                {BYPASS_CODE}
              </code>
              <button className="btn" onClick={copyCode}>{copied ? 'Copied' : 'Copy'}</button>
              <button className="btn" onClick={reset}>Done</button>
            </div>
            <div style={{ fontSize: 12.5, color: '#166534', marginTop: 10, lineHeight: 1.5 }}>
              Give the user this code to type in place of a Duo push. Single use, expires in ~1 hour.
            </div>
          </div>
        )}
        {result && result.status === 'refused' && (
          <div style={{ marginTop: 16, border: '1px solid #FCD34D', background: '#FFFBEB', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#92400E', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div><strong>Refused.</strong> {result.detail || 'This user could not be found in Duo, or a code could not be created.'}</div>
            <button className="btn" onClick={() => setResult(null)}>Dismiss</button>
          </div>
        )}
        {result && result.status === 'error' && (
          <div style={{ marginTop: 16, border: '1px solid #FCA5A5', background: '#FEF2F2', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#991B1B', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div><strong>Something went wrong.</strong> {result.detail || 'Try again.'}</div>
            <button className="btn" onClick={() => setResult(null)}>Dismiss</button>
          </div>
        )}

        {/* History */}
        <div style={{ marginTop: 28 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>Recent bypass codes</div>
          {history === null ? (
            <div style={{ fontSize: 13, color: 'var(--text-subtle)' }}>Loading…</div>
          ) : history.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text-subtle)' }}>Nothing created yet.</div>
          ) : (
            <div style={{ border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden', overflowY: 'auto', maxHeight: 420, background: 'var(--bg-elev)' }}>
              {history.map((h, i) => {
                const s = STATUS_STYLE[h.status] || STATUS_STYLE.error;
                return (
                  <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: i < history.length - 1 ? '1px solid var(--line)' : 'none' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: s.color, background: s.bg, padding: '2px 9px', borderRadius: 999, whiteSpace: 'nowrap' }}>{s.label}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.targetUpn}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--text-subtle)' }}>by {h.issuerName || h.issuerEmail} · {fmtWhen(h.createdAt)}{h.detail ? ` · ${h.detail}` : ''}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
